import { add } from 'date-fns';
import { HotelForm as Form } from 'hotel/HotelForm';
import { Field } from 'react-final-form';

import { ClientTeamMember, RosterRequestType } from '../../generated/graphql';
import { useAppSelector } from '../../store/store';
import getEnumLabel from '../../utilities/getEnumLabel';
import getEnumValues from '../../utilities/getEnumValues';
import { parseDateForPicker } from '../../utilities/parseDateForPicker';
import { getControlFieldLabelForPod } from '../../utilities/podBasedFiltering';
import { required } from '../../utilities/validators';
import { PrimaryButton } from '../Button';
import FormDateField from '../Inputs/FormInputs/FormDateField';
import FormDropdown from '../Inputs/FormInputs/FormDropdown';
import TextField from '../Inputs/FormInputs/TextField';

export type RequestChangesFormValues = {
  requestType: RosterRequestType;
  guests?: ClientTeamMember[];
  details: string;
  checkInDate?: string;
  checkOutDate?: string;
  controlNumber: string;
};

type RequestChangesFormProps = {
  onSubmit: (values: RequestChangesFormValues) => void;
  initialValues?: Partial<RequestChangesFormValues>;
  readonly?: boolean;
};

export default function RequestChangesForm({
  onSubmit,
  initialValues,
  readonly = false,
}: RequestChangesFormProps) {
  const { rosterRequestType } = useAppSelector((state) => state.enums);
  const { pod: clientPod } = useAppSelector((state) => state.clientConfig);

  const requestTypeOptions = getEnumValues(rosterRequestType).filter(
    (type) => type !== RosterRequestType.NewReservation,
  );

  return (
    <Form onSubmit={onSubmit} initialValues={initialValues}>
      {({ handleSubmit, values }) => (
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-2 text-primaryGray sm:col-start-1">
              <Field
                labelText="Change Type"
                name="requestType"
                placeholder="Select change type"
                disabled={readonly}
                validate={required}
                component={FormDropdown}
                options={requestTypeOptions}
                renderOption={(option: string) => {
                  return (
                    <span className="block truncate">
                      {getEnumLabel(rosterRequestType, option)}
                    </span>
                  );
                }}
              />
            </div>

            <div className="col-span-2 text-primaryGray sm:col-start-1">
              <Field
                labelText={getControlFieldLabelForPod(clientPod)}
                name="controlNumber"
                placeholder={`The ${getControlFieldLabelForPod(clientPod).toLowerCase()} this request is associated with.`}
                disabled={readonly}
                component={TextField}
              />
            </div>

            {values.requestType === RosterRequestType.DateChange ? (
              <>
                <div className="col-span-2 text-primaryGray sm:col-start-1">
                  <Field
                    labelText="Check-in date"
                    name={`checkInDate`}
                    component={FormDateField}
                    placeholderText="-"
                    disabled={readonly}
                    validate={required}
                    selectsStart
                  />
                </div>

                <div className="col-span-2 text-primaryGray">
                  <Field
                    labelText="Check-out date"
                    name={`checkOutDate`}
                    component={FormDateField}
                    placeholderText="-"
                    disabled={readonly}
                    validate={required}
                    selectsEnd
                    minDate={add(
                      parseDateForPicker(values?.checkInDate) ?? new Date(),
                      {
                        days: 1,
                      },
                    )}
                  />
                </div>
              </>
            ) : null}

            <div className="col-span-4 text-primaryGray sm:col-start-1">
              <Field
                labelText={
                  values.requestType === RosterRequestType.DateChange
                    ? 'Details'
                    : 'Details (Required)'
                }
                name="details"
                validate={
                  values.requestType === RosterRequestType.DateChange
                    ? undefined
                    : required
                }
                disabled={readonly}
                component={TextField}
                multiline
                rows={6}
              />
            </div>

            {!readonly && (
              <div className="col-span-6">
                <PrimaryButton text="Submit request" type="submit" />
              </div>
            )}
          </div>
        </form>
      )}
    </Form>
  );
}
